import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { Permissions as P } from '@fas/types';
import { Card, CardDescription, CardHeader, CardTitle } from '@fas/ui';
import { NAV_ITEMS, visibleNavItems } from '@/components/layout/nav';
import { FacultyToday } from '@/features/attendance/FacultyToday';
import { TodayCard } from '@/features/scheduling/TodayCard';
import { hasAny, useMe } from '@/lib/permissions';
import { labelize } from '@/lib/format';

function greeting(timeZone: string): string {
  const hour = Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone }).format(new Date()));
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Landing page (§31, §55.2): for faculty, today's classes with clock-in/out, daily attendance
 * and cut-off totals; for everyone, shortcuts to the areas their role can use.
 */
export function DashboardPage() {
  const me = useMe();
  if (!me) return null;
  const tz = me.institution?.timezone ?? 'Asia/Manila';
  const name = me.faculty?.fullName ?? me.user.displayName ?? me.user.email;
  const today = new Intl.DateTimeFormat('en-PH', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: tz }).format(new Date());
  const shortcuts = visibleNavItems(NAV_ITEMS, me).filter((i) => i.to !== '/');
  const attendance = Boolean(me.faculty) && hasAny(me.permissions, [P.ATTENDANCE_READ_SELF]);
  const scheduleOnly = !attendance && Boolean(me.faculty) && hasAny(me.permissions, [P.SCHEDULE_READ_SELF]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-slate-600">{today}</p>
        <h1 className="text-2xl font-semibold tracking-tight">
          {greeting(tz)}, {name}
        </h1>
      </div>

      {me.faculty ? (
        <Card>
          <CardHeader>
            <CardTitle>Your faculty profile</CardTitle>
            <CardDescription>
              {me.faculty.employeeNumber} · {labelize(me.faculty.employmentTypeCode)} ·{' '}
              {me.faculty.attendanceModel === 'DAILY_AND_SESSION' ? 'Daily attendance and class sessions' : 'Attendance per scheduled class or duty'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {attendance ? <FacultyToday /> : null}
      {scheduleOnly ? <TodayCard /> : null}

      {shortcuts.length > 0 ? (
        <section aria-labelledby="shortcuts-title">
          <h2 id="shortcuts-title" className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Shortcuts
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {shortcuts.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-blue-300 hover:bg-blue-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                >
                  <item.icon className="size-5 text-blue-700" aria-hidden />
                  <span className="flex-1 font-medium">{item.label}</span>
                  <ArrowRight className="size-4 text-slate-400" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
